import { useState } from 'react';
import { API_URL, API_VERSION } from '@constants';
import { paths } from '../../types/api';
import { ApiError, useApiConfig } from '@hooks/useApiConfig';
import { useData } from '@hooks/useData';

const ENDPOINT_URL = '/two-factor-auth/verify';
const ENDPOINT_URL_WITH_VERSION = `${API_VERSION}${ENDPOINT_URL}` as const;
type url = typeof ENDPOINT_URL_WITH_VERSION;

export type Google2FAVerifyInput = paths[url]['post']['requestBody']['content']['application/json'];

export interface google2FAVerifySuccessResponse {
    data: paths[url]['post']['responses']['200']['content']['application/json'];
}

/**
 * Requests endpoint to register a new user account.
 */
export const useGoogle2FAVerify = () => {
    const { getHeaders, getCommonParameters, handleCommonErrors } = useApiConfig();
    const { setLogin } = useData();
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);
    const [lastResponseTime, setLastResponseTime] = useState<Date | null>(null);

    const request = async (props: Google2FAVerifyInput) => {
        setLoading(true);
        setError(null);
        setSuccess(null);

        try {
            const response = await fetch(`${API_URL}${ENDPOINT_URL}`, {
                method: 'POST',
                headers: getHeaders(),
                body: JSON.stringify({ ...getCommonParameters(), ...props }),
            });

            if (!response.ok) {
                const json = await response.json();
                const error = new Error(json.message || '') as ApiError;
                error.statusCode = response.status;
                throw error;
            }

            const body = await response.json();
            const successMessage =
                typeof (body as { message?: string })?.message === 'string'
                    ? (body as { message?: string }).message
                    : typeof (body as { data?: { message?: string } })?.data?.message === 'string'
                      ? (body as { data?: { message?: string } }).data?.message ?? null
                      : null;
            setSuccess(successMessage ?? null);

            const data: google2FAVerifySuccessResponse = body;

            setLogin(data);
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } catch (err: any) {
            handleCommonErrors(err);
            if (err.message) {
                setError(err.message);
            }
        } finally {
            setLoading(false);
            setLastResponseTime(new Date());
        }
    };

    return { request, loading, error, success, lastResponseTime };
};
